import { AgGridReact } from '@ag-grid-community/react';
import { useAgGrid } from '@tassat/ag-grid';
import {
    AgGridServerFilterMap,
    TransactionStatusCode,
    TransactionTypeString,
    transactionTypeCharMap
} from '@tassat/common-definitions';
import { Transaction } from '@tassat/fred-definitions';
import React from 'react';

// Cell Renderers
import { AG_GRID_SMALL_HEADER_HEIGHT } from 'Lib/constant';
import { auditTimeSort } from 'Lib/stringHelpers';
import { useAppSelector } from 'Reducers/store/hooks';
import { getCurrentRole, getUser } from 'Selectors/common';
import { adminTransactionsMapping, clientTransactionsMapping } from 'Selectors/transactions';
import ExternalFilter from 'SharedComponents/agGrid/filters/externalFilter';
import AdminFirmWalletCellRenderer from 'SharedComponents/cellRenderComponent/adminFirmWalletCellRenderer';
import ClientCounterpartyWalletCellRenderer from 'SharedComponents/cellRenderComponent/clientCounterpartyCellRenderer';
import ClientCounterpartyFirmCellRenderer from 'SharedComponents/cellRenderComponent/clientCounterpartyFirmCellRenderer';
import DateCellRender from 'SharedComponents/cellRenderComponent/datecellrender';
import DirectionCellRender from 'SharedComponents/cellRenderComponent/directionCellRender';
import ExternalFilterHeaderComponent from 'SharedComponents/cellRenderComponent/externalFilterHeaderComponent';
import FormatAssetsAmount from 'SharedComponents/cellRenderComponent/formatAssetsAmount';
import FormatCurrentBalance from 'SharedComponents/cellRenderComponent/formatCurrentBalance';
import MultiLineCellRenderer from 'SharedComponents/cellRenderComponent/multiLineCellRenderer';
import { TextCellRender } from 'SharedComponents/cellRenderComponent/textCellrender';
import TransactionStatusCellRenderer from 'SharedComponents/cellRenderComponent/transactionStatusCellRenderer';
import { TransactionTypeConversion } from 'SharedComponents/cellRenderComponent/transactionTypes';
import UserIdCellRenderer from 'SharedComponents/cellRenderComponent/userIdCellRenderer';
import { useFeatures } from 'SharedComponents/hooks/useFeatures';

import {
    adminTransactionTypeOptions,
    clientTransactionTypeOptions,
    getAdminTransactionHistoryColumnDefs,
    getClientTransactionHistoryColumnDefs,
    getMappedColumn
} from './transactionHistoryColumnDefs';

import type { TransactionHistoryAgGridProps } from './';

import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-material.css';
import 'SharedComponents/agGrid/styles/agGridOverrides.css';

export function TransactionHistoryAgGrid (props: TransactionHistoryAgGridProps) {
    const {
        tableOptions,
        setSelectedTransactionId,
        overlayOpen
    } = props;
    const currentUser = useAppSelector(getUser);
    const transactions = useAppSelector((state) => (currentUser?.IsAdmin ?
        adminTransactionsMapping(state)
        : clientTransactionsMapping(state)));
    const currentRole = useAppSelector(getCurrentRole);

    const [features] = useFeatures();
    const [ gridApi, onGridReady ] = useAgGrid();
    const [ sortedTransactions, setSortedTransactions ] = React.useState<Transaction[]>(transactions);

    React.useEffect(() => {
        if (gridApi) {
            gridApi.api.onFilterChanged();
        }
    }, [ gridApi, tableOptions.TableFilters.length, tableOptions.Sort, tableOptions.DefaultState ]);

    React.useEffect(() => {
        let mappedTransactions = transactions;
        const dateConditionArray = tableOptions.DateCondition ?
            tableOptions.DateCondition.split(':')
            : [];

        const dateArray = dateConditionArray.map((date) => {
            const dateSplit = date.split('|');

            return dateSplit;
        });

        if (dateArray.length) {
            mappedTransactions = mappedTransactions.filter(
                (transaction: Transaction) => {
                    let date;
                    for (let i = 0; i < dateArray.length; i++) {
                        const dateFilter = dateArray[i];
                        if (dateFilter[1] === AgGridServerFilterMap.greaterThan) {
                            date = transaction.AuditTime >= dateFilter[2];
                            if (!date) {
                                break;
                            }
                        }
                    }

                    return date;
                }
            );
        }

        if (tableOptions.SelectedWallet && tableOptions.SelectedWallet !== 'none') {
            mappedTransactions = mappedTransactions.filter(
                (transaction: Transaction) => transaction.SenderAddress === tableOptions.SelectedWallet ||
                    transaction.RecipientAddress === tableOptions.SelectedWallet
            );
        }

        if (tableOptions.Sort === 'auditTime|DESC') {
            mappedTransactions.sort(auditTimeSort);
        }

        setSortedTransactions(mappedTransactions);
    }, [
        transactions,
        tableOptions.DateCondition,
        tableOptions.Sort,
        tableOptions.SelectedFirmName,
        tableOptions.SelectedWallet,
        tableOptions.SelectedFirm
    ]);

    const selectTransaction = (e) => {
        const { data } = e;
        setSelectedTransactionId(`${data.RequestId}-${data.type}`);
    };

    const getBalance = (params) => {
        const tranType: string = (transactionTypeCharMap(params.data.type) as string);
        let senderBalance = true;

        if (
            tranType === TransactionTypeString.receive
        ) {
            senderBalance = false;
        }

        return senderBalance ? {
            Amount: params.data.SenderAvailableBalance,
            Currency: params.data.CurCode
        } : {
            Amount: params.data.RecipientAvailableBalance,
            Currency: params.data.CurCode
        };
    };

    const columnDefs = React.useMemo(() => {
        if (currentUser?.IsAdmin) {
            return getAdminTransactionHistoryColumnDefs({
                features,
                currentUser,
                currentRole,
                tableOptions,
                getMappedColumn,
                getBalance,
                adminTransactionTypeOptions
            });
        }

        return getClientTransactionHistoryColumnDefs({
            features,
            currentUser,
            currentRole,
            tableOptions,
            getMappedColumn,
            getBalance,
            clientTransactionTypeOptions
        });
    }, [
        features,
        currentUser,
        currentRole,
        tableOptions,
        getBalance
    ]);

    return (
        <div
            className='ag-theme-material'
            id='myGrid'
            style={{
                minWidth: '100%',
                height: '100%'
            }}
        >
            <AgGridReact
                animateRows={false}
                columnDefs={columnDefs}
                columnMenu='legacy'
                components={{
                    externalFilterHeader: ExternalFilterHeaderComponent,
                    directionCellRender: DirectionCellRender,
                    dateCellRender: DateCellRender,
                    formatAssetsAmount: FormatAssetsAmount,
                    transType: TransactionTypeConversion,
                    tranStatus: TransactionStatusCellRenderer,
                    counterpartyWallet: ClientCounterpartyWalletCellRenderer,
                    formatCurrentBalance: FormatCurrentBalance,
                    counterpartyFirm: ClientCounterpartyFirmCellRenderer,
                    firmWallet: AdminFirmWalletCellRenderer,
                    multiLineText: MultiLineCellRenderer,
                    textCell: TextCellRender,
                    userIdCell: UserIdCellRenderer,
                    externalFilter: ExternalFilter
                }}
                defaultColDef={{
                    resizable: true,
                    sortable: false,
                    minWidth: 80
                }}
                getRowId={({ data }) => `${data.RequestId}-${data.type}`}
                headerHeight={AG_GRID_SMALL_HEADER_HEIGHT}
                onGridReady={onGridReady}
                onRowClicked={selectTransaction}
                rowClassRules={{
                    'lockedRow': ({ data }) => (
                        [
                            TransactionStatusCode.FAILED,
                            TransactionStatusCode.REJECTED
                        ].includes(data.Status)
                    ),
                    'pendingRow': ({ data }) => data.Status === TransactionStatusCode.PENDING_APPROVAL
                }}
                rowData={sortedTransactions}
                rowHeight={currentUser?.IsAdmin ? 60 : undefined}
                suppressCellFocus={true}
                suppressContextMenu={true}
                suppressNoRowsOverlay={overlayOpen}
            />
        </div>
    );
}